import { ElementIcon, ElementIconType } from "@/components/ui/icons";
import { selectHierarchicalRegistrationStatusMap } from "@/data-preparation-tool/store/registration-status-selectors";
import { doesEntityRequireReview } from "@/data-preparation-tool/utils/entity-utils";
import { useAppSelector } from "@/store/store-hooks";
import { yellow } from "@faro-lotv/flat-ui";
import {
  CaptureTreeEntity,
  CaptureTreeEntityType,
  CaptureTreeEntityTypeHint,
  isCaptureTreeEntityRevision,
  isRevisionScanEntity,
  RegistrationStatus,
} from "@faro-lotv/service-wires";
import { Badge } from "@mui/material";
import { SxProps } from "@mui/system";
import { useMemo, type JSX } from "react";

interface EntityIconProps {
  /** entity to show the icon for */
  entity: CaptureTreeEntity;

  /** styles to apply to the icon */
  sx: SxProps;
}

/** @returns icon for a capture tree entity */
export function EntityIcon({ entity, sx }: EntityIconProps): JSX.Element {
  const statusMap = useAppSelector(selectHierarchicalRegistrationStatusMap);

  const requiresReview = useMemo(() => {
    if (
      isCaptureTreeEntityRevision(entity) &&
      (isRevisionScanEntity(entity) ||
        entity.type === CaptureTreeEntityType.cluster)
    ) {
      return doesEntityRequireReview(entity, statusMap);
    }
    return false;
  }, [entity, statusMap]);

  return (
    <CaptureTreeEntityIcon
      sx={sx}
      badgeVisible={requiresReview}
      entity={entity}
    />
  );
}

type ScanEntityIconProps = EntityIconProps & {
  /** whether the badge should be visible */
  badgeVisible: boolean;
};

/** @returns the label component used, for each node, in the tree with the node's icon, name and context menu */
function CaptureTreeEntityIcon({
  sx,
  badgeVisible,
  entity,
}: ScanEntityIconProps): JSX.Element {
  const iconType = iconForCaptureTreeElement(entity);
  return (
    <Badge
      variant="dot"
      overlap="circular"
      invisible={!badgeVisible}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{
        "& .MuiBadge-dot": {
          mt: "1px",
          minWidth: "6px",
          width: "6px",
          minHeight: "6px",
          height: "6px",
          backgroundColor: yellow[600],
          border: "none",
        },
      }}
    >
      <ElementIcon icon={iconType} sx={{ ...sx, fontSize: "1.125em" }} />
    </Badge>
  );
}

/**
 * Compute the icon for registration capture tree node
 *
 * @param entity entity in the capture tree
 * @returns the icon to use for this element
 */
export function iconForCaptureTreeElement(
  entity: CaptureTreeEntity | undefined,
): ElementIconType {
  switch (entity?.type) {
    case CaptureTreeEntityType.cluster:
      // Custom icons for special clusters
      switch (entity.typeHint) {
        // Site scape captures are viewed as a single scan from the user perspective
        case CaptureTreeEntityTypeHint.siteScapeCapture:
          return ElementIconType.ScanIcon;
      }
      // Add lock icon for locked clusters
      if (entity.registrationStatus === RegistrationStatus.lockedManually) {
        return ElementIconType.ScanClusterLockIcon;
      }
      // Default cluster icon
      return ElementIconType.ScanClusterIcon;
    case CaptureTreeEntityType.photogrammetryScan:
      // ASSUMPTION: Photogrammetry scan = Drone capture, this is how it's marketed / sold.
      // See https://faro01.atlassian.net/browse/NRT-2901
      return ElementIconType.DroneScanIcon;
    case CaptureTreeEntityType.orbisScan:
    case CaptureTreeEntityType.roviScan:
      return ElementIconType.ScanMobileIcon;
    case CaptureTreeEntityType.orbisFlashScan:
      return ElementIconType.ScanFlashIcon;
    case CaptureTreeEntityType.elsScan:
    case CaptureTreeEntityType.focusScan:
    default:
      return ElementIconType.ScanIcon;
  }
}
